import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Cloud, Shield, Zap, Globe, DollarSign, Clock, Server, Lock, TrendingUp, Award, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCTA from '../components/ProductCTA';

const CloudHostingPage = () => {
  const stats = [
    { value: "99.995%", label: "Uptime Guarantee" },
    { value: "24x7", label: "Monitoring" },
    { value: "9", label: "Global Locations" },
    { value: "4.9/5", label: "Satisfaction" }
  ];

  const benefits = [
    { 
      icon: Zap, 
      title: "Instant Scalability", 
      strapline: "Grow without limits",
      desc: "Scale resources up or down instantly to match business growth and workload demands without traditional infrastructure delays or capital investment."
    },
    { 
      icon: Shield, 
      title: "Enterprise Security", 
      strapline: "Protected and compliant",
      desc: "End-to-end encryption, multi-factor authentication, continuous monitoring, and compliance with GDPR and ISO standards for complete peace of mind."
    },
    { 
      icon: TrendingUp, 
      title: "Optimised Performance", 
      strapline: "Speed that surpasses expectations",
      desc: "Enhanced ERP processing speed with 99.995% uptime reliability, surpassing typical on-premise environments with AWS-powered infrastructure."
    },
    { 
      icon: DollarSign, 
      title: "Cost Efficiency", 
      strapline: "Smart spending, maximum value",
      desc: "Pay-as-you-grow pricing reduces upfront investment in hardware and IT staff, lowering total cost of ownership by up to 40%."
    },
    { 
      icon: Globe, 
      title: "Global Accessibility", 
      strapline: "Anywhere, anytime, any device",
      desc: "Access SAP Business One securely from anywhere, on any device, facilitating remote work, global operations, and real-time collaboration."
    },
    { 
      icon: Server, 
      title: "Business Continuity", 
      strapline: "Always on, never compromised",
      desc: "Integrated disaster recovery, automated backups, and system monitoring ensure your critical business operations remain available 24x7."
    }
  ];

  const awsAdvantages = [
    { title: "No Server Management", desc: "We handle all infrastructure - you focus on your business" },
    { title: "Automatic Updates", desc: "Latest SAP updates deployed seamlessly without disruption" },
    { title: "Scalable Resources", desc: "Add users and data without performance degradation" },
    { title: "Nightly Backups", desc: "Automated backups with point-in-time recovery" },
    { title: "Disaster Recovery", desc: "Built-in redundancy and failover capabilities" },
    { title: "Security Monitoring", desc: "24x7 threat detection and security updates" }
  ];

  const locations = [
    { name: "United Kingdom", region: "London", code: "eu-west-2" },
    { name: "Ireland", region: "Dublin", code: "eu-west-1" },
    { name: "Germany", region: "Frankfurt", code: "eu-central-1" },
    { name: "USA", region: "N. Virginia", code: "us-east-1" },
    { name: "Canada", region: "Toronto", code: "ca-central-1" },
    { name: "UAE", region: "Dubai", code: "me-central-1" },
    { name: "South Africa", region: "Cape Town", code: "af-south-1" },
    { name: "Australia", region: "Sydney", code: "ap-southeast-2" },
    { name: "Singapore", region: "Singapore", code: "ap-southeast-3" }
  ];

  const faqs = [
    { q: "What is cloud hosting for SAP Business One?", a: "Cloud hosting for SAP Business One means your ERP system runs on remote servers managed by Ascarii on AWS infrastructure, rather than on your own on-premise hardware. You access the system securely over the internet from any device. Ascarii handles all server management, updates, backups, and monitoring on your behalf." },
    { q: "What does 99.995% uptime actually mean in practice?", a: "99.995% uptime equates to less than 26 minutes of unplanned downtime per year. This is enterprise-grade availability achieved through AWS multi-region infrastructure, automated failover, redundant systems, and 24x7 monitoring. By comparison, a typical on-premise server typically delivers around 99% uptime, which is over 87 hours of potential downtime annually." },
    { q: "Where is my SAP Business One data stored?", a: "Ascarii hosts SAP Business One across 9 global AWS regions covering five continents. UK customers default to London (eu-west-2) or Ireland (eu-west-1), with EU options in Frankfurt (eu-central-1). Additional regions are available in the USA, Canada, UAE, South Africa, Australia, and Singapore. This ensures full GDPR compliance and UK data sovereignty. Data never leaves your chosen region without your explicit consent." },
    { q: "Is my SAP Business One data secure in the cloud?", a: "Yes. Ascarii cloud hosting uses AWS enterprise-grade security including end-to-end encryption in transit and at rest, multi-factor authentication, role-based access controls, continuous threat monitoring, and intrusion detection. The infrastructure is ISO 27001 compliant and meets GDPR requirements. Automated daily backups with point-in-time recovery protect against data loss." },
    { q: "Can I access my cloud-hosted SAP Business One from anywhere?", a: "Yes. Cloud-hosted SAP Business One is accessible from any internet-connected device including laptops, desktop PCs, and via the SAP Business One mobile app on smartphones and tablets. Remote and hybrid working is fully supported without the need for a VPN in most deployment configurations." },
    { q: "What happens if there is a problem with the system?", a: "Ascarii provides 24x7 proactive monitoring that detects and responds to infrastructure issues before they impact your business. For application issues, our support team is available with response times from under 2 hours for critical issues. We maintain disaster recovery environments that can restore operations rapidly in the event of a major incident." },
    { q: "How does cloud hosting for SAP Business One compare to on-premise?", a: "Cloud hosting eliminates capital expenditure on servers and reduces IT overhead - there are no servers to maintain, patch, or replace. You pay a predictable monthly fee that includes infrastructure, monitoring, backups, and support. On-premise gives you direct control of hardware but requires IT resource and capital investment. Most Ascarii customers find cloud hosting delivers lower total cost of ownership within 2 to 3 years." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One Cloud Hosting on AWS | 99.995% Uptime Guarantee"
        description="Enterprise-grade cloud hosting for SAP B1 on AWS infrastructure. 99.995% uptime SLA, automated backups, bank-grade security. 9 global locations including UK data centres."
        keywords="SAP B1 cloud hosting, AWS SAP hosting, cloud ERP UK, SAP Business One AWS, managed cloud hosting"
        canonical="https://www.ascarii.com/cloud-hosting"
        faqSchema={faqs}
      />

      {/* Hero */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-sky-600 via-blue-700 to-cyan-600">
        <div className="absolute inset-0 z-0">
          <img src="/hero-background.jpeg" alt="Cloud Sky" className="w-full h-full object-cover opacity-40" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">SAP Business One on AWS</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Your Business in the Cloud</h1>
              <p className="text-xl md:text-2xl text-sky-100 mb-8 leading-relaxed">
                Enterprise-grade cloud hosting with 99.995% uptime, 24x7 monitoring, and global accessibility. Focus on growing your business while we manage your infrastructure.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-sky-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-sky-800 via-blue-900 to-sky-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Cloud Hosting on AWS</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-sky-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AWS Advantages */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AWS Cloud Advantages</h2>
            <p className="text-xl text-gray-600">Enterprise infrastructure without the enterprise overhead</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awsAdvantages.map((adv, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} className="flex items-start gap-4 p-6 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{adv.title}</h3>
                  <p className="text-sm text-gray-600">{adv.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Locations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">9 Global AWS Locations</h2>
            <p className="text-xl text-gray-600">Nine SAP Business One-ready regions across five continents - full data sovereignty in every region</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-6">
            {locations.map((loc, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-xl border-2 border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all text-center">
                <Globe className="w-12 h-12 text-sky-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-1">{loc.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{loc.region}</p>
                <code className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded">{loc.code}</code>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Security */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">Your Data, Your Control</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Complete Data Sovereignty</h2>
              <p className="text-lg text-gray-600 mb-6">Your data belongs to you - always. We provide the infrastructure and expertise while you maintain complete ownership and control.</p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Data Sovereignty</h4>
                    <p className="text-sm text-gray-600">Your data stays in your chosen region with complete transparency</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Data Portability</h4>
                    <p className="text-sm text-gray-600">Move freely with no vendor lock-in or data restrictions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Complete Ownership</h4>
                    <p className="text-sm text-gray-600">You own your data - we never share or access without permission</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=85" alt="Data Security" className="w-full h-[400px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-green-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=85" alt="Cost Efficiency" className="w-full h-[400px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Cost Optimisation</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Reduce IT Costs by Up to 40%</h2>
              <p className="text-lg text-gray-600 mb-6">Pay-as-you-grow pricing with no upfront hardware investment or ongoing infrastructure costs.</p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">No server hardware to purchase or maintain</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">No IT staff needed for infrastructure management</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Predictable monthly costs with no surprises</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Automatic updates and upgrades included</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Scale costs with business growth - pay only for what you use</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" id="faqs">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-4">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Common questions about SAP Business One cloud hosting</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }} viewport={{ once: true }} className="bg-gray-50 p-6 rounded-xl border border-slate-200 hover:border-sky-200 hover:shadow-md transition-all">
                <h3 className="text-base font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-sky-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Move to the Cloud Today</h2>
              <p className="text-xl text-gray-600 mb-8">Join businesses achieving 99.995% uptime and 40% cost savings</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Cloud className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">AWS Powered</h4>
                    <p className="text-gray-600 text-sm">Enterprise infrastructure</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Award className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Proven Reliability</h4>
                    <p className="text-gray-600 text-sm">99.995% uptime</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center">
                    <Target className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Expert Support</h4>
                    <p className="text-gray-600 text-sm">24x7 monitoring</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One Cloud Hosting" brandColor="sky-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CloudHostingPage;
